import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { softDeleteContacto, type ContactoItem } from '@/shared/api/controllers/contactos';
import { CONTACTOS_QUERY_KEY } from './queries';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactoItem | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function DeleteContactDialog({
  open,
  onOpenChange,
  contact,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => softDeleteContacto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTACTOS_QUERY_KEY });
      onOpenChange(false);
      onSuccess('Contacto eliminado correctamente.');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'No se pudo eliminar el contacto.';
      onOpenChange(false);
      onError(message);
    },
  });

  if (!contact) return null;
  const nombre = `${contact.fields.Nombre_C ?? ''} ${contact.fields.Apellido_C ?? ''}`.trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando contacto…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar contacto</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{nombre || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate(contact.id)}
            disabled={mutation.isPending}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
